import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  A2vAssetType,
  AssetsSortDirection,
  AssetsSortProperty,
  defaultAssetsFilter,
  type AssetsFilter,
} from './model';

export type AssetsRouteSearch = {
  search?: string;
  assetType?: A2vAssetType;
  tags?: string;
  page?: number;
  size?: number;
  sortProperty?: AssetsSortProperty | null;
  sortDirection?: AssetsSortDirection;
};

export function assetsRouteSearchSchema(search: Record<string, unknown>): AssetsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const rawAssetType = optionalTrimmedString(search['assetType']);
  const rawDirection = optionalTrimmedString(search['sortDirection']);
  const tags = optionalTrimmedString(search['tags']);
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const assetType = Object.values(A2vAssetType).find((value) => value === rawAssetType);
  const sortProperty = Object.values(AssetsSortProperty).find((value) => value === rawSortProperty);
  const sortDirection = Object.values(AssetsSortDirection).find((value) => value === rawDirection);

  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(assetType ? { assetType } : {}),
    ...(tags ? { tags } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseAssetsRouteSearch(search: AssetsRouteSearch): AssetsFilter {
  return {
    searchTerm: search.search ?? defaultAssetsFilter.searchTerm,
    assetType: search.assetType ?? defaultAssetsFilter.assetType,
    tags: parseAssetTags(search.tags),
    page: search.page ? search.page - 1 : defaultAssetsFilter.page,
    size: search.size ?? defaultAssetsFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultAssetsFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultAssetsFilter.sortDirection,
  };
}

export function serializeAssetsRouteSearch(filter: AssetsFilter): AssetsRouteSearch {
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(filter.assetType ? { assetType: filter.assetType } : {}),
    ...(filter.tags.length ? { tags: filter.tags.map(encodeURIComponent).join(',') } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultAssetsFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultAssetsFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultAssetsFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

function parseAssetTags(value: string | undefined) {
  if (!value) return [];
  return value
    .split(',')
    .map(decodeAssetTag)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function decodeAssetTag(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
